import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Calendar, FileText, MessageSquare, Receipt, Briefcase, LogOut, Home, ShieldCheck,
} from "lucide-react";

const PORTAL_NAV = [
  { to: "/portal", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/portal/services", icon: Briefcase, label: "My Services" },
  { to: "/portal/credit-repair", icon: ShieldCheck, label: "Credit Repair" },
  { to: "/portal/appointments", icon: Calendar, label: "Appointments" },
  { to: "/portal/invoices", icon: Receipt, label: "Invoices" },
  { to: "/portal/documents", icon: FileText, label: "Documents" },
  { to: "/portal/messages", icon: MessageSquare, label: "Messages" },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "C")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-soft">
      <header data-testid="portal-header" className="sticky top-0 z-30 backdrop-blur-xl bg-soft/80 border-b border-navy/5">
        <div className="container-app flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <Logo surface="light" to={null} testid="portal-logo" size="h-10 md:h-11" />
            <span className="hidden md:inline-flex text-[10px] uppercase tracking-[0.25em] text-gold border-l border-navy/10 pl-3">Client Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm text-navy/70 hover:text-navy">
              <Home className="w-4 h-4" /> Public site
            </Link>
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gold text-white flex items-center justify-center text-xs font-medium">{initials}</div>
              )}
              <div className="hidden sm:block leading-tight">
                <div className="text-sm text-navy">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-navy/50">{user?.email}</div>
              </div>
              <button
                data-testid="portal-logout"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-navy/60 hover:text-navy transition p-2"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3" data-testid="portal-sidebar">
          <nav className="card p-2 lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {PORTAL_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-3 text-sm whitespace-nowrap transition ${
                    isActive ? "bg-navy text-soft" : "text-navy/70 hover:bg-navy/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
